import { useCallback, useEffect, useRef, useState } from "react";
import { decodeEventLog } from "viem";

import { useBlockNumber } from "./useBlockNumber";
import { useRpcRequest } from "./useRpcRequest";

import type { Address, Hex } from "viem";

export interface DecodedEvent {
  blockNumber: number;
  timestamp: number;
  transactionHash: string;
  logIndex: number;
  address: Address;
  contractName: string | null;
  eventName: string | null;
  args: Record<string, unknown> | null;
  topics: Hex[];
  data: Hex;
}

interface UseContractEventsOptions {
  abis: Record<string, readonly unknown[]>;
  addressNames: Record<string, string>;
  maxEvents?: number;
  fromBlock?: number;
}

export function useContractEvents({
  abis,
  addressNames,
  maxEvents = 2000,
  fromBlock,
}: UseContractEventsOptions) {
  const { data: blockNumber } = useBlockNumber();
  const { request } = useRpcRequest();
  const [events, setEvents] = useState<DecodedEvent[]>([]);
  const lastFetchedBlock = useRef(0);
  const appliedFrom = useRef<number | undefined>(undefined);

  const resolveContractName = useCallback(
    (addr: string): string | null => {
      const lower = addr.toLowerCase();
      for (const [address, name] of Object.entries(addressNames)) {
        if (address.toLowerCase() === lower) return name;
      }
      return null;
    },
    [addressNames],
  );

  const tryDecodeEvent = useCallback(
    (log: {
      topics: Hex[];
      data: Hex;
    }): { eventName: string; args: Record<string, unknown> } | null => {
      for (const abi of Object.values(abis)) {
        try {
          const result = decodeEventLog({
            abi: abi as Parameters<typeof decodeEventLog>[0]["abi"],
            topics: log.topics as [`0x${string}`, ...`0x${string}`[]],
            data: log.data,
          });
          return {
            eventName: result.eventName,
            args: result.args as Record<string, unknown>,
          };
        } catch {
          // ABI doesn't match
        }
      }
      return null;
    },
    [abis],
  );

  useEffect(() => {
    if (!blockNumber) return;

    const bn = Number(blockNumber);

    // If fromBlock became available after initial polling started, back-fill
    // missed historical events so this view matches sidebar badge counts.
    let backfillStart: number | undefined;
    if (
      fromBlock !== undefined &&
      appliedFrom.current === undefined &&
      lastFetchedBlock.current > 0 &&
      fromBlock < lastFetchedBlock.current
    ) {
      backfillStart = Math.max(1, fromBlock);
      appliedFrom.current = fromBlock;
    }

    if (fromBlock !== undefined && appliedFrom.current === undefined) {
      appliedFrom.current = fromBlock;
    }

    if (bn <= lastFetchedBlock.current && backfillStart === undefined) return;

    const startBlock =
      lastFetchedBlock.current === 0
        ? Math.max(1, fromBlock ?? bn - 5)
        : lastFetchedBlock.current + 1;

    async function decodeLogs(
      logs: {
        blockNumber: string;
        address: string;
        topics: Hex[];
        data: Hex;
        transactionHash: string;
        logIndex: string;
      }[],
    ): Promise<DecodedEvent[]> {
      if (logs.length === 0) return [];

      const blockTimestamps = new Map<number, number>();
      const blockNumbers = [
        ...new Set(logs.map((log) => parseInt(log.blockNumber, 16))),
      ];

      await Promise.allSettled(
        blockNumbers.map(async (blockNum) => {
          const block = (await request("eth_getBlockByNumber", [
            "0x" + blockNum.toString(16),
            false,
          ])) as { timestamp?: string } | null;

          if (block?.timestamp) {
            blockTimestamps.set(blockNum, parseInt(block.timestamp, 16));
          }
        }),
      );

      return logs.map((log) => {
        const logBlockNum = parseInt(log.blockNumber, 16);
        const addr = log.address as Address;
        const decoded = tryDecodeEvent({
          topics: log.topics,
          data: log.data,
        });

        return {
          blockNumber: logBlockNum,
          timestamp: blockTimestamps.get(logBlockNum) ?? 0,
          transactionHash: log.transactionHash,
          logIndex: parseInt(log.logIndex, 16),
          address: addr,
          contractName: resolveContractName(addr),
          eventName: decoded?.eventName ?? null,
          args: decoded?.args ?? null,
          topics: log.topics,
          data: log.data,
        };
      });
    }

    async function fetchEvents() {
      const newEvents: DecodedEvent[] = [];

      try {
        // Back-fill historical range if deployment block arrived after polling.
        if (backfillStart !== undefined) {
          const backfillEnd = startBlock - 1;
          if (backfillStart <= backfillEnd) {
            const backfillLogs = await request("eth_getLogs", [
              {
                fromBlock: "0x" + backfillStart.toString(16),
                toBlock: "0x" + backfillEnd.toString(16),
              },
            ]);

            if (Array.isArray(backfillLogs)) {
              newEvents.push(
                ...(await decodeLogs(
                  backfillLogs as {
                    blockNumber: string;
                    address: string;
                    topics: Hex[];
                    data: Hex;
                    transactionHash: string;
                    logIndex: string;
                  }[],
                )),
              );
            }
          }
        }

        const logs = await request("eth_getLogs", [
          {
            fromBlock: "0x" + startBlock.toString(16),
            toBlock: "0x" + bn.toString(16),
          },
        ]);

        if (!Array.isArray(logs)) {
          lastFetchedBlock.current = bn;
          return;
        }

        newEvents.push(
          ...(await decodeLogs(
            logs as {
              blockNumber: string;
              address: string;
              topics: Hex[];
              data: Hex;
              transactionHash: string;
              logIndex: string;
            }[],
          )),
        );
      } catch {
        // ignore fetch errors
      }

      if (newEvents.length > 0) {
        setEvents((prev) => {
          const unique = new Map<string, DecodedEvent>();

          for (const event of [...prev, ...newEvents]) {
            unique.set(
              `${event.transactionHash}:${String(event.logIndex)}`,
              event,
            );
          }

          return [...unique.values()]
            .sort((a, b) => {
              if (a.blockNumber !== b.blockNumber) {
                return a.blockNumber - b.blockNumber;
              }
              return a.logIndex - b.logIndex;
            })
            .slice(-maxEvents);
        });
      }

      lastFetchedBlock.current = bn;
    }

    void fetchEvents();
  }, [
    blockNumber,
    request,
    maxEvents,
    fromBlock,
    tryDecodeEvent,
    resolveContractName,
  ]);

  const clear = useCallback(() => {
    setEvents([]);
  }, []);

  return { events, clear };
}
